import type { Node as ProseMirrorNode } from "prosemirror-model";
import type { EditorView } from "prosemirror-view";

import { EDITOR_MODE } from "../constants";
import { insertImageNodesAtPosition } from "../image-placement";
import {
  createSourceFragment,
  formatSourceMarkdownImage,
} from "../markdown/source-utils";

const isImageFile = (file: File) => file.type.startsWith("image/");

const getImageAlt = (file: File) => file.name || "Pasted image";

const dedupeFiles = (files: File[]) => {
  const seen = new Set<string>();

  return files.filter((file) => {
    const key = [file.name, file.type, file.size].join(":");

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

export const getImageFilesFromDataTransfer = (dataTransfer: DataTransfer) =>
  Array.from(dataTransfer.files).filter(isImageFile);

export const getImageFilesFromClipboardData = (clipboardData: DataTransfer) => {
  const files = Array.from(clipboardData.files ?? []).filter(isImageFile);
  const itemFiles = Array.from(clipboardData.items ?? [])
    .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null && isImageFile(file));

  return dedupeFiles([...files, ...itemFiles]);
};

const updateImageNode = (
  view: EditorView,
  src: string,
  attrs: Record<string, unknown>,
) => {
  let targetPos: number | null = null;

  view.state.doc.descendants((node, pos) => {
    if (node.type.name !== "image" || node.attrs.src !== src) {
      return;
    }

    targetPos = pos;
    return false;
  });

  if (targetPos == null) {
    return;
  }

  const node = view.state.doc.nodeAt(targetPos);

  if (!node) {
    return;
  }

  view.dispatch(
    view.state.tr.setNodeMarkup(targetPos, undefined, {
      ...node.attrs,
      ...attrs,
    }),
  );
};

const replaceSourceText = (
  view: EditorView,
  fromText: string,
  toText: string,
) => {
  let targetFrom: number | null = null;

  view.state.doc.descendants((node, pos) => {
    if (!node.isText || !node.text) {
      return true;
    }

    const index = node.text.indexOf(fromText);

    if (index < 0) {
      return true;
    }

    targetFrom = pos + index;
    return false;
  });

  if (targetFrom == null) {
    return;
  }

  view.dispatch(
    view.state.tr.insertText(toText, targetFrom, targetFrom + fromText.length),
  );
};

const uploadSourceImage = ({
  file,
  objectUrl,
  uploadImageFile,
  view,
}: {
  file: File;
  objectUrl: string;
  uploadImageFile: (file: File) => Promise<string | null>;
  view: EditorView;
}) => {
  void uploadImageFile(file)
    .then((url) => {
      if (!url) {
        return;
      }

      replaceSourceText(view, objectUrl, url);
    })
    .catch(() => undefined)
    .finally(() => {
      URL.revokeObjectURL(objectUrl);
    });
};

const uploadRichImage = ({
  file,
  objectUrl,
  uploadImageFile,
  view,
}: {
  file: File;
  objectUrl: string;
  uploadImageFile: (file: File) => Promise<string | null>;
  view: EditorView;
}) => {
  void uploadImageFile(file)
    .then((url) => {
      if (url) {
        updateImageNode(view, objectUrl, {
          src: url,
          uploadStatus: null,
        });
        return;
      }

      updateImageNode(view, objectUrl, {
        uploadStatus: "error",
      });
    })
    .catch(() => {
      updateImageNode(view, objectUrl, {
        uploadStatus: "error",
      });
    })
    .finally(() => {
      URL.revokeObjectURL(objectUrl);
    });
};

export const insertImageFilesIntoEditor = ({
  files,
  insertAt,
  mode,
  uploadImageFile,
  view,
}: {
  files: File[];
  insertAt: number;
  mode: EDITOR_MODE;
  uploadImageFile?: (file: File) => Promise<string | null>;
  view: EditorView;
}) => {
  if (!files.length) {
    return false;
  }

  const objectUrls = files.map((file) => URL.createObjectURL(file));

  if (mode === EDITOR_MODE.SIMPLE) {
    const markdownImages = files.map((file, index) =>
      formatSourceMarkdownImage({
        alt: getImageAlt(file),
        src: objectUrls[index],
      }),
    );

    view.dispatch(
      view.state.tr
        .replaceWith(
          insertAt,
          insertAt,
          createSourceFragment(markdownImages.join("\n\n")),
        )
        .scrollIntoView(),
    );

    if (uploadImageFile) {
      files.forEach((file, index) => {
        uploadSourceImage({
          file,
          objectUrl: objectUrls[index],
          uploadImageFile,
          view,
        });
      });
    }

    return true;
  }

  const imageNodes: ProseMirrorNode[] = files.map((file, index) =>
    view.state.schema.nodes.image.create({
      alt: getImageAlt(file),
      src: objectUrls[index],
      title: null,
      uploadStatus: uploadImageFile ? "uploading" : null,
    }),
  );

  view.dispatch(
    insertImageNodesAtPosition(
      view.state.tr,
      insertAt,
      imageNodes,
    ).scrollIntoView(),
  );

  if (uploadImageFile) {
    files.forEach((file, index) => {
      uploadRichImage({
        file,
        objectUrl: objectUrls[index],
        uploadImageFile,
        view,
      });
    });
  }

  return true;
};
